import React, { useState, useRef, useEffect } from 'react'

import { createPortal } from 'react-dom'
import './ApplyV2Tooltip.scss'

interface Props {
    children: React.ReactNode
    label: string
    isDisabled?: boolean
    openDelay?: number
    closeDelay?: number
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'bottom-start' | 'bottom-end' | 'top-end'
    maxWidth?: string
    display?: 'inline' | 'block' | 'inline-block'
    style?: React.CSSProperties
}

const baseClass = 'ApplyV2Tooltip'

function ApplyV2Tooltip({
    children,
    label,
    isDisabled = false,
    openDelay = 500,
    closeDelay = 100,
    placement = 'top',
    maxWidth,
    display = 'inline-block',
    style,
}: Props) {
    const [isOpen, setIsOpen] = useState(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)
    const triggerRef = useRef<HTMLDivElement>(null)

    const handleMouseEnter = () => {
        if (isDisabled) return

        clearTimeout(timeoutRef.current as NodeJS.Timeout)

        timeoutRef.current = setTimeout(() => {
            setIsOpen(true)
        }, openDelay)
    }

    const handleMouseLeave = () => {
        clearTimeout(timeoutRef.current as NodeJS.Timeout)

        timeoutRef.current = setTimeout(() => {
            setIsOpen(false)
        }, closeDelay)
    }

    useEffect(() => {
        const handleScroll = () => {
            if (isOpen) {
                setIsOpen(false)
            }
        }

        window.addEventListener('scroll', handleScroll, true)

        return () => {
            window.removeEventListener('scroll', handleScroll, true)
            clearTimeout(timeoutRef.current as NodeJS.Timeout)
        }
    }, [isOpen])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isOpen])

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const handleFocus = (e: React.FocusEvent) => {
        setIsOpen(false)
        e.preventDefault()
        e.stopPropagation()
    }

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsOpen(false)
        e.preventDefault()
        e.stopPropagation()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault()
        }
    }

    const triggerRect = triggerRef.current?.getBoundingClientRect()
    const tooltipStyle =
        triggerRect &&
        ({
            // ...(placement === 'top' && {
            //     left: triggerRect.left + triggerRect.width / 2,
            //     top: triggerRect.top - 8,
            //     transform: 'translate(-50%, -100%)',
            // }),
            ...(placement === 'top' && {
                left: triggerRect.left + triggerRect.width / 2,
                top: triggerRect.top - 8,
                transform: 'translate(-50%, -100%)',
                // maxWidth: maxWidth || '100%',
            }),
            ...(placement === 'top-start' && {
                left: triggerRect.left,
                top: triggerRect.top - 8,
                transform: 'translate(0, -100%)',
            }),
            ...(placement === 'top-end' && {
                left: triggerRect.right,
                top: triggerRect.top - 8,
                transform: 'translate(-100%, -100%)',
            }),
            ...(placement === 'bottom' && {
                left: triggerRect.left + triggerRect.width / 2,
                top: triggerRect.bottom + 8,
                transform: 'translateX(-50%)',
            }),
            ...(placement === 'bottom-start' && {
                left: triggerRect.left,
                top: triggerRect.bottom + 8,
                transform: 'translateX(0)',
            }),
            ...(placement === 'bottom-end' && {
                left: triggerRect.right,
                top: triggerRect.bottom + 8,
                transform: 'translateX(-100%)',
            }),
            ...(placement === 'left' && {
                left: triggerRect.left - 8,
                top: triggerRect.top + triggerRect.height / 2,
                transform: 'translate(-100%, -50%)',
            }),
            ...(placement === 'right' && {
                left: triggerRect.right + 8,
                top: triggerRect.top + triggerRect.height / 2,
                transform: 'translateY(-50%)',
            }),
        } as React.CSSProperties)

    return (
        <div
            className={baseClass}
            ref={tooltipRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            onFocus={handleFocus}
            onTouchStart={handleTouchStart}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            style={{ outline: 'none', display, ...style }}
            role="tooltip"
            aria-label={label}
        >
            <div
                className={`${baseClass}__trigger`}
                ref={triggerRef}
                style={{ display }}
                aria-describedby={isOpen ? `${baseClass}__content` : undefined}
            >
                {children}
            </div>

            {isOpen &&
                label &&
                triggerRef.current &&
                tooltipStyle &&
                createPortal(
                    <div
                        id={`${baseClass}__content`}
                        className={`${baseClass}__tooltip ${baseClass}__tooltip--${placement} ${baseClass}__tooltip--visible`}
                        role="tooltip"
                        style={{
                            position: 'fixed',
                            ...tooltipStyle,
                            maxWidth: maxWidth || 'auto',
                        }}
                    >
                        <div className={`${baseClass}__arrow`} />
                        <div className={`${baseClass}__content`}>{label}</div>
                    </div>,
                    document.body
                )}
        </div>
    )
}

export default ApplyV2Tooltip
